import { ChangeDetectionStrategy, Component } from '@angular/core';

@Component({
    selector: 'juyo-terms-of-service-content',
    styleUrl: './terms-of-service-content.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <article class="tos">
            <section>
                <h3>1. Introduction and Acceptance</h3>
                <p>
                    These Terms of Service (the “Terms”) govern the Customer’s access to and use of HotelGPT, a
                    conversational AI analytics service operated by JUYO BV (“JUYO”), registered under company number
                    0552.628.004, with registered office in Belgium.
                </p>
                <p>
                    HotelGPT enables hotels and hospitality businesses to connect data sources and obtain AI-generated
                    analyses, graphs, recommendations, and textual insights about their operational and commercial data
                    (the “Service”).
                </p>
                <p>
                    By creating an account and clicking “I Agree” during registration, the Customer accepts these Terms
                    in full. If the Customer does not agree, the Customer must not create an account or use the Service.
                    Continued use of the Service after any update to these Terms constitutes acceptance of the updated
                    Terms.
                </p>
            </section>

            <section>
                <h3>2. Definitions</h3>
                <p><strong>«Account»</strong>: the Customer’s registered HotelGPT account, including credentials and associated settings.</p>
                <p>
                    <strong>«Admin User»</strong>: the Authorised User who created the Organisation and who holds administrative
                    privileges, including the ability to invite or remove Authorised Users and to transfer admin rights
                    to another Authorised User.
                </p>
                <p>
                    <strong>«Authorised Users»</strong>: all individuals invited by the Admin User to access the Service under the
                    Customer’s Organisation.
                </p>
                <p>
                    <strong>«Consumption»</strong>: the metered usage of the Service beyond the Complimentary Period, measured in
                    Kassandra Tokens.
                </p>
                <p>
                    <strong>«Customer»</strong>: the legal entity or natural person on whose behalf the Organisation is created and
                    who is bound by these Terms.
                </p>
                <p>
                    <strong>«Customer Data»</strong>: all data uploaded to, connected with, or generated within the Service by or on
                    behalf of the Customer, including hotel operational data, booking data, and any personal data
                    contained therein, but excluding Aggregated Data as defined in Article 8.5.
                </p>
                <p>
                    <strong>«Complimentary Period»</strong>: the initial allocation of Kassandra Tokens provided at no charge upon
                    Organisation creation, as published on the Service’s pricing page. The Complimentary Period is a
                    promotional offer and may be modified or discontinued by JUYO at any time.
                </p>
                <p>
                    <strong>«Kassandra Tokens»</strong>: the unit of measurement used to meter usage of the Service, as described on
                    the pricing page. Kassandra Token consumption may vary based on the complexity of queries, volume of
                    data processed, and AI model used. Kassandra Tokens are distinct from the technical tokens used by
                    the underlying AI models.
                </p>
                <p>
                    <strong>«Organisation»</strong>: the Customer’s workspace within the Service, created during registration, under
                    which all Authorised Users, Customer Data, and billing are grouped.
                </p>
                <p>
                    <strong>«Service»</strong>: the HotelGPT conversational AI analytics platform, including all AI Features,
                    dashboards, reports, and related functionality made available by JUYO.
                </p>
                <p>
                    <strong>«AI Features»</strong>: any functionality of the Service that uses artificial intelligence or
                    machine-learning technologies to generate insights, forecasts, explanations, recommendations,
                    graphs, or summaries.
                </p>
            </section>

            <section>
                <h3>3. Account Registration and Organisation Setup</h3>
                <h4>Organisation Creation</h4>
                <p>
                    3.1. To access the Service, the Admin User must create an Organisation by providing accurate and
                    complete registration information (including the organisation name) and accepting these Terms via
                    the online sign-up process. By creating the Organisation, the Admin User represents and warrants that
                    they have the authority to bind the Customer to these Terms.
                </p>
                <p>
                    3.2. The Admin User holds administrative privileges for the Organisation, including the ability to
                    invite and remove Authorised Users. The Admin User may transfer admin rights to another Authorised
                    User at any time.
                </p>
                <h4>Authorised Users</h4>
                <p>
                    3.3. Additional users may only join an existing Organisation upon invitation by the Admin User.
                    Invited users must create their own Account and accept these Terms, including the Acceptable Use
                    obligations set out in Article 6, before accessing the Service.
                </p>
                <p>
                    3.4. The Customer is responsible for all activities occurring under its Organisation, including the
                    actions of all Authorised Users.
                </p>
                <h4>Duplicate Organisations</h4>
                <p>
                    3.5. It is possible for multiple Organisations to be created for the same hotel, hotel group, or
                    legal entity. Each Organisation is treated as an independent Customer under separate Terms. JUYO is
                    not obligated to detect, prevent, or merge duplicate Organisations. It is the Customer’s
                    responsibility to manage its Organisation and user access.
                </p>
                <h4>Account Security</h4>
                <p>
                    3.6. Each user is responsible for maintaining the confidentiality of their Account credentials and
                    must promptly notify JUYO of any unauthorised use.
                </p>
                <p>
                    3.7. JUYO reserves the right to refuse registration, suspend, or terminate any Account or
                    Organisation at its reasonable discretion, including where the Customer provides inaccurate
                    information or violates these Terms.
                </p>
            </section>

            <section>
                <h3>4. Service Description and License</h3>
                <p>
                    4.1. For the duration set forth in Article 13, JUYO grants the Customer a revocable, non-exclusive,
                    non-transferable, non-sublicensable, worldwide right to access and use the Service for the
                    Customer’s internal business purposes, subject to these Terms.
                </p>
                <p>
                    4.2. The Service allows the Customer to connect hotel data sources and use AI Features to query,
                    analyse, and visualise data. Outputs may include deep analyses, graphs, recommendations, and textual
                    summaries.
                </p>
                <p>
                    4.3. The Service is provided as a hosted SaaS solution accessible via web browser. No software is
                    installed on the Customer’s systems.
                </p>
                <p>
                    4.4. The Customer acknowledges that JUYO may monitor and review interactions between the Customer
                    (including its Authorised Users) and the Service, including questions submitted and AI-generated
                    responses, for the purposes of quality assurance, product improvement, troubleshooting, and support.
                    Where such interactions contain personal data, this processing is carried out in accordance with the
                    Data Processing Agreement and applicable data-protection laws.
                </p>
            </section>

            <section>
                <h3>5. Pricing and Payment</h3>
                <h4>General</h4>
                <p>
                    5.1. The Service is available under the pricing plans published on the Service’s pricing page,
                    available at https://juyo.ai/pricing. JUYO may offer a Complimentary Period with an initial
                    allocation of Kassandra Tokens at no charge or through another promotional offer format. The terms of
                    any such offer will be communicated at the time of the promotion and may be modified or discontinued
                    at any time.
                </p>
                <p>
                    5.2. Kassandra Tokens are allocated on a monthly basis, reset on the anniversary of the Customer’s
                    sign-up date. Unused Kassandra Tokens expire at the end of each monthly billing cycle and do not
                    carry over to the following month. This applies to tokens received under the Complimentary Period and
                    under monthly or yearly subscription plans. Kassandra Tokens purchased through a Top Up Bundle are
                    subject to the expiry terms set out in Article 5.8.
                </p>
                <p>
                    5.3. JUYO will provide a usage dashboard within the Service showing the Customer’s current Kassandra
                    Token balance and consumption history.
                </p>
                <h4>Pay-as-You-Go</h4>
                <p>
                    5.4. Under the pay-as-you-go plan, the Customer’s Kassandra Token consumption is metered and billed
                    after use at the rates published on the pricing page. There is no minimum purchase, no subscription,
                    and no recurring charges.
                </p>
                <h4>Monthly Subscription</h4>
                <p>
                    5.5. Under the monthly subscription plan, the Customer receives a recurring Kassandra Token
                    allocation each month in exchange for a fixed monthly fee, as published on the pricing page, billed
                    monthly in advance. The subscription renews automatically each month and may be cancelled by the
                    Customer at any time. Upon cancellation, the subscription remains active until the end of the current
                    billing period.
                </p>
                <h4>Yearly Subscription</h4>
                <p>
                    5.6. Under the yearly subscription plan, the Customer commits to a twelve (12) month term in exchange
                    for a discounted monthly rate, billed annually in advance. The Customer receives the same monthly
                    Kassandra Token allocation as the monthly subscription plan. If the Customer cancels during the
                    yearly term, access to the Service and the monthly Kassandra Token allocation continue until the end
                    of the prepaid twelve-month period. No pro-rata refund will be issued for the remaining months.
                </p>
                <p>
                    5.7. If the Customer needs to disconnect data sources or systems from the Service ahead of the end
                    of the term (for example, due to a property sale or change of ownership), the Customer may do so via
                    the self-service disconnect function in the Service or by contacting JUYO.
                </p>
                <h4>Top Up Bundles</h4>
                <p>
                    5.8. The Customer may purchase additional Kassandra Tokens at any time through a Top Up Bundle,
                    regardless of the Customer’s current plan (including pay-as-you-go and all subscription plans). Top Up
                    Bundles are available at the rates published on the pricing page. Kassandra Tokens purchased through a
                    Top Up Bundle are valid for thirty (30) days from the date of purchase, after which any unused tokens
                    expire. Top Up Bundles are non-refundable once purchased.
                </p>
                <h4>Payment and Invoicing</h4>
                <p>
                    5.9. Payments are processed via Stripe. Available payment methods are displayed at checkout. For
                    pay-as-you-go usage, the Customer will be billed after consumption. For subscriptions and Top Up
                    Bundles, payment is due at the time of purchase or at the start of each billing cycle, as applicable.
                </p>
                <p>
                    5.10. An automated VAT-compliant invoice will be issued for each payment and made available in the
                    Customer’s Account.
                </p>
                <p>
                    5.11. In case of a failed or overdue payment, JUYO may suspend access to the Service in accordance
                    with Article 13.4(b) until the outstanding amount is settled. Statutory interest on late payments
                    shall apply in accordance with Belgian law.
                </p>
                <p>
                    5.12. All prices are exclusive of applicable taxes and VAT, which shall be added where required by
                    law.
                </p>
                <h4>Price Changes</h4>
                <p>5.13. JUYO reserves the right to modify pricing. The way price changes take effect depends on the Customer’s plan:</p>
                <ul>
                    <li>Pay-as-you-go: updated prices apply to future Kassandra Token consumption as published on the pricing page. No separate notification is required.</li>
                    <li>Monthly subscription: JUYO will notify the Customer at least thirty (30) days before the new price takes effect. The new price applies from the next billing cycle after the notice period.</li>
                    <li>Yearly subscription: price changes apply only upon renewal. JUYO will notify the Customer before the renewal date.</li>
                </ul>
                <p>
                    5.14. Price changes will not apply retroactively to Kassandra Tokens already consumed or purchased,
                    or to the remaining term of a yearly subscription already in effect.
                </p>
                <h4>Inactivity</h4>
                <p>
                    5.15. If the Customer’s Organisation shows no usage of the Service for a continuous period of two (2)
                    months, JUYO reserves the right to disconnect the Customer’s data sources and terminate the
                    agreement by providing fifteen (15) days’ prior written notice (email is sufficient). The Customer
                    may prevent termination by resuming use of the Service within the notice period.
                </p>
            </section>

            <section>
                <h3>6. Acceptable Use</h3>
                <p>6.1. The Customer shall use the Service only for lawful purposes and in accordance with these Terms.</p>
                <p>6.2. The Customer shall not:</p>
                <ul>
                    <li>reverse engineer, decompile, disassemble, or attempt to discover the source code, algorithms, or underlying structure of the Service;</li>
                    <li>modify, translate, or create derivative works based on the Service;</li>
                    <li>use the Service to process data on behalf of third parties or offer it as a service to third parties;</li>
                    <li>upload data that is unlawful, infringing, or that the Customer does not have the right to process;</li>
                    <li>attempt to circumvent usage limits, security measures, or access controls;</li>
                    <li>use the Service in any manner that could damage, disable, or impair the Service.</li>
                </ul>
                <p>
                    6.3. The Customer shall ensure that all Authorised Users comply with these Terms. The Customer is
                    liable for any breach by its Authorised Users.
                </p>
            </section>

            <section>
                <h3>7. Artificial Intelligence</h3>
                <h4>Nature of the Service</h4>
                <p>
                    7.1. The Customer acknowledges that the Service is an AI-powered analytics product. All core
                    functionality of the Service — including data analysis, insights, forecasts, recommendations, graphs,
                    and summaries — is generated using artificial intelligence and/or machine-learning technologies (“AI
                    Features”) provided by JUYO and by third-party AI providers integrated into the Service.
                </p>
                <p>
                    7.2. JUYO will provide a clear description of the main capabilities and limitations of the AI
                    Features within the user interface and/or the Service’s documentation.
                </p>
                <h4>Customer Responsibilities</h4>
                <p>7.3. The Customer acknowledges and agrees that:</p>
                <ul>
                    <li>AI-generated outputs depend directly on the questions, prompts, instructions, quality of underlying data, and context provided by the Customer;</li>
                    <li>AI-generated outputs are inherently probabilistic and may contain errors, omissions, inaccuracies, hallucinations, or inconsistencies;</li>
                    <li>the Customer shall ensure appropriate human review and oversight of all AI-generated outputs and shall not rely exclusively on such outputs for decisions that may have significant legal, financial, operational, or human-resources consequences;</li>
                    <li>the Customer is responsible for verifying AI-generated outputs before using them in business decisions or communicating them to third parties.</li>
                </ul>
                <h4>Data Use in AI Features</h4>
                <p>
                    7.4. JUYO will use Customer Data in AI Features only for the purpose of providing the Service and in
                    accordance with the Data Processing Agreement and applicable data-protection laws. Unless expressly
                    agreed in writing, JUYO will not use Customer Data to train or improve general-purpose AI models for
                    the benefit of third parties. For the avoidance of doubt, JUYO’s use of Aggregated Data as defined in
                    Article 8.5 shall not constitute use of Customer Data for the purposes of this Article 7.4.
                </p>
                <p>
                    7.5. Where the provision of AI Features involves the transfer of personal data to third-country AI
                    providers, JUYO will ensure that an appropriate legal transfer mechanism is in place under applicable
                    data-protection law (such as an adequacy decision, Data Privacy Framework participation, and/or
                    standard contractual clauses).
                </p>
                <h4>Third-Party AI Services</h4>
                <p>
                    7.6. The Customer acknowledges that the Service relies on third-party AI services. To the extent such
                    services are provided by third parties, they are subject to the terms and policies of those
                    providers. JUYO does not provide any independent guarantee regarding the availability, performance,
                    or continued provision of such third-party AI services.
                </p>
                <h4>Fair Use</h4>
                <p>
                    7.7. The Customer agrees to use the Service in a manner that is reasonable and consistent with
                    ordinary business purposes. JUYO reserves the right to introduce or adjust usage limits at any time
                    upon reasonable prior notice to the Customer, including where such adjustment is necessary due to
                    capacity constraints, changes in third-party AI provider terms, or to ensure fair access across its
                    customer base.
                </p>
                <h4>AI Liability</h4>
                <p>
                    7.8. Without prejudice to the general limitations of liability in Article 12, the Customer expressly
                    acknowledges that AI-generated outputs are inherently probabilistic. JUYO shall not be liable for any
                    loss, damage, cost, or claim (whether direct or indirect) arising from or in connection with:
                </p>
                <ul>
                    <li>the Customer’s reliance on, interpretation of, or use of any AI-generated output, including where such output is incomplete, incorrect, misleading, outdated, or inconsistent;</li>
                    <li>the Customer’s failure to exercise appropriate human review, supervision, or oversight when using AI-generated outputs, or the Customer’s use of such outputs as the sole basis for operational, financial, legal, HR, or strategic decisions;</li>
                    <li>any inaccuracies, hallucinations, inconsistencies, or technical limitations inherent in AI models or third-party AI systems integrated into the Service;</li>
                    <li>the content, structure, or quality of the questions, prompts, instructions, or context provided by the Customer, including misunderstandings or misinterpretations arising from such inputs;</li>
                    <li>the inaccuracy, incompleteness, unsuitability, or unlawfulness of Customer Data or other data provided or selected by the Customer, whether used directly or indirectly by AI Features.</li>
                </ul>
                <p>Nothing in this clause excludes liability that cannot be excluded under mandatory law.</p>
                <h4>Assistive Nature</h4>
                <p>
                    7.9. AI-generated content is provided solely as an assistive functionality and does not constitute
                    professional advice, authoritative guidance, or a decision-making substitute.
                </p>
                <h4>Updates to AI Features</h4>
                <p>
                    7.10. JUYO may update this AI clause and/or the configuration of AI Features to reflect changes in
                    applicable law or in the terms of its third-party AI providers. Where such changes materially affect
                    the Customer’s use of the Service, JUYO will notify the Customer under the change-of-terms provisions
                    of Article 15.3.
                </p>
            </section>

            <section>
                <h3>8. Intellectual Property</h3>
                <p>
                    8.1. JUYO is and remains the owner of all intellectual property rights in and to the Service,
                    including the Software, algorithms, models, user interface, documentation, and all related materials.
                </p>
                <p>
                    8.2. Nothing in these Terms shall be construed as an assignment or transfer of any intellectual
                    property rights to the Customer. The Customer’s right to use the Service is limited to the license
                    granted in Article 4.
                </p>
                <p>
                    8.3. The Customer retains all rights in Customer Data. By using the Service, the Customer grants JUYO
                    a limited license to process Customer Data solely for the purpose of providing the Service.
                </p>
                <p>8.4. The Customer shall not reproduce any element of the Service or its documentation by any means.</p>
                <h4>8.5 Anonymized and Aggregated Data</h4>
                <p>
                    JUYO may derive anonymized and aggregated data from Customer Data, including but not limited to data
                    relating to product usage, commercial performance, financial metrics, and operational activity
                    (“Aggregated Data”). Aggregated Data shall be irreversibly de-identified such that it cannot
                    reasonably be used to identify the Customer or any individual. JUYO may use Aggregated Data without
                    restriction for purposes including but not limited to: industry benchmarking, market analysis,
                    product improvement, research, and the development of new services. For the avoidance of doubt,
                    Aggregated Data is not considered Customer Data under these Terms, and JUYO’s right to use Aggregated
                    Data shall survive termination or expiration of these Terms.
                </p>
            </section>

            <section>
                <h3>9. Confidentiality</h3>
                <p>
                    9.1. Each party shall keep confidential all information received from the other party in connection
                    with the Service, and shall not disclose it to third parties other than employees or agents who need
                    to know, nor use it for any purpose other than exercising rights and fulfilling obligations under
                    these Terms. For the avoidance of doubt, JUYO’s confidentiality obligations in this Article 9 do not
                    apply to Aggregated Data as defined in Article 8.5.
                </p>
                <p>
                    9.2. The obligations in Article 9.1 do not apply to information that: (a) is or becomes publicly
                    available without fault of the receiving party; (b) is independently developed by the receiving
                    party; (c) was known to the receiving party before disclosure; (d) is legitimately received from a
                    third party without a confidentiality obligation; or (e) must be disclosed by law or court order.
                </p>
                <p>
                    9.3. Confidentiality obligations shall survive termination of these Terms for a period of three (3)
                    years.
                </p>
            </section>

            <section>
                <h3>10. Data Protection</h3>
                <p>
                    10.1. Each party shall comply with all applicable data protection laws, including the General Data
                    Protection Regulation (EU 2016/679) and all national implementing legislation.
                </p>
                <p>
                    10.2. Where JUYO processes personal data on behalf of the Customer, the Data Processing Agreement
                    published at https://juyo.ai/data-processing-agreement shall apply and is incorporated into these
                    Terms by reference. The DPA may be updated from time to time; material changes will be notified to the
                    Customer in accordance with Article 15.3.
                </p>
                <p>
                    10.3. The Customer is solely responsible for ensuring it has a valid legal basis for any personal
                    data it uploads to or processes through the Service.
                </p>
                <p>
                    10.4. The Customer acknowledges that the Service may process the following categories of personal
                    data: user account data (name, email, login credentials), usage and interaction data, booking and
                    reservation data, guest-related data (where uploaded by the Customer), and operational/transactional
                    hotel data.
                </p>
            </section>

            <section>
                <h3>11. Switching and Data Portability</h3>
                <p>
                    11.1. In accordance with Regulation (EU) 2023/2854 (the Data Act), the Customer may at any time
                    request the export of Customer Data or switch to an alternative provider.
                </p>
                <p>
                    11.2. JUYO will not impose contractual, technical, or commercial obstacles that unduly hinder
                    switching and will provide reasonable cooperation.
                </p>
                <p>
                    11.3. Upon written request, JUYO will make available a copy of Customer Data in a structured,
                    commonly used, machine-readable format (CSV, JSON, or equivalent) within twenty (20) business days.
                </p>
            </section>

            <section>
                <h3>12. Limitation of Liability</h3>
                <p>12.1. Nothing in these Terms excludes or limits liability that cannot be excluded under applicable law.</p>
                <p>
                    12.2. To the maximum extent permitted by law, JUYO shall not be liable for: (a) indirect, incidental,
                    special, or consequential damages; (b) loss of profits, data, or business opportunities; (c) damages
                    arising from the Customer’s reliance on AI-generated outputs; or (d) circumstances beyond JUYO’s
                    reasonable control.
                </p>
                <p>
                    12.3. JUYO’s total cumulative liability for all claims arising under or in connection with these
                    Terms shall not exceed the total fees paid by the Customer during the twelve (12) months preceding
                    the event giving rise to the claim, or one thousand euros (€1,000), whichever is greater.
                </p>
                <p>
                    12.4. The Service is provided “as is” and “as available.” JUYO makes no warranties, express or
                    implied, including warranties of merchantability, fitness for a particular purpose, or
                    non-infringement, to the fullest extent permitted by law. The Service relies on third-party
                    infrastructure and AI providers (including but not limited to Anthropic and OpenAI), and JUYO does
                    not warrant the uninterrupted availability, accuracy, or performance of such third-party services.
                </p>
            </section>

            <section>
                <h3>13. Term and Termination</h3>
                <p>
                    13.1. These Terms enter into force upon Organisation creation and remain in effect for as long as the
                    Customer maintains an active Organisation.
                </p>
                <p>
                    13.2. Either party may terminate these Terms at any time by giving thirty (30) days’ written notice
                    (email is sufficient).
                </p>
                <p>
                    13.3. The Admin User may terminate immediately by closing the Organisation through the Service’s
                    account settings.
                </p>
                <p>
                    13.4. JUYO may suspend or terminate the Customer’s access immediately if: (a) the Customer breaches
                    any material provision of these Terms; (b) the Customer fails to pay any amount due within fifteen
                    (15) days of the first payment reminder sent by JUYO; or (c) required by law or a competent authority.
                </p>
                <p>
                    13.5. Upon termination: (a) the Customer’s access to the Service will cease within forty-eight (48)
                    hours; (b) the Customer may request export of Customer Data under Article 11 within thirty (30) days
                    of termination, and JUYO will deliver the export within twenty (20) business days of the request; (c)
                    after the data export period, JUYO will anonymise and/or securely delete Customer Data, except where
                    retention is required by law.
                </p>
                <p>
                    13.6. Termination shall not affect any rights or obligations that have accrued before the effective
                    date of termination, including any outstanding payment obligations.
                </p>
            </section>

            <section>
                <h3>14. Force Majeure</h3>
                <p>
                    14.1. Neither party shall be liable for failure or delay in performing obligations under these Terms
                    to the extent caused by circumstances beyond its reasonable control (force majeure), including
                    natural disasters, epidemics, strikes, cyber-attacks, network delays or intrusions, failure of
                    telecommunications infrastructure, or government actions.
                </p>
                <p>
                    14.2. The affected party shall notify the other party promptly and use reasonable efforts to mitigate
                    the impact.
                </p>
                <p>
                    14.3. If a force majeure event persists for more than sixty (60) days, either party may terminate
                    these Terms without compensation.
                </p>
            </section>

            <section>
                <h3>15. General Provisions</h3>
                <p>
                    15.1. These Terms, together with the Data Processing Agreement (published at
                    https://juyo.ai/data-processing-agreement) and the pricing page, constitute the entire agreement
                    between the parties with respect to the Service.
                </p>
                <p>
                    15.2. If any provision is declared void or unenforceable, the remaining provisions shall remain in
                    full force and effect.
                </p>
                <p>
                    15.3. JUYO may update these Terms from time to time. Material changes will be notified at least
                    thirty (30) days in advance via email or in-app notification. Continued use after the effective date
                    of changes constitutes acceptance.
                </p>
                <p>
                    15.4. The Customer may not assign or transfer any rights under these Terms without JUYO’s prior
                    written consent.
                </p>
                <p>15.5. JUYO may use subcontractors in providing the Service and remains responsible for their performance.</p>
                <p>
                    15.6. The parties are authorised to communicate publicly about the existence of the Customer’s use of
                    HotelGPT, without disclosing economic conditions, and may use each other’s name and logo in good
                    faith for this purpose.
                </p>
                <p>15.7. These Terms are governed by Belgian law, excluding its conflict of laws rules.</p>
                <p>
                    15.8. Any dispute shall first be resolved through negotiation or mediation. If unresolved within
                    thirty (30) days, the dispute shall be submitted to the exclusive jurisdiction of the Courts of
                    Brussels.
                </p>
            </section>
        </article>
    `,
})
export class TermsOfServiceContentComponent {}
